import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { sprintAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trophy, Target, Zap, Clock, TrendingUp, TrendingDown, 
  Minus, Award, BookOpen, BarChart3, Flame, Calendar, Brain,
  ArrowUpDown, ArrowUp, ArrowDown, GraduationCap
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

export default function SprintStats() {
  useSEO('Sprint Statistics | JepPrep', 'Analyze your sprint history, trends, and category performance over time.');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [categorySortField, setCategorySortField] = useState('total'); // 'total', 'accuracy', 'name'
  const [categorySortDir, setCategorySortDir] = useState('desc'); // 'asc', 'desc'

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await sprintAPI.getComprehensiveStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load statistics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ms) => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const tenths = Math.floor((ms % 1000) / 100);
    return `${seconds}.${tenths}s`;
  };

  const formatPlayTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'declining': return <TrendingDown className="w-4 h-4 text-rose-400" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-emerald-400';
    if (accuracy >= 60) return 'text-yellow-300';
    if (accuracy >= 40) return 'text-amber-400';
    return 'text-rose-400';
  };

  const getMasteryLevel = (accuracy, total) => {
    if (total < 5) return { level: 'Novice', color: 'bg-slate-500' };
    if (accuracy >= 90) return { level: 'Master', color: 'bg-purple-500' };
    if (accuracy >= 75) return { level: 'Expert', color: 'bg-sky-500' };
    if (accuracy >= 60) return { level: 'Proficient', color: 'bg-emerald-500' };
    if (accuracy >= 40) return { level: 'Learning', color: 'bg-yellow-500' };
    return { level: 'Beginner', color: 'bg-orange-500' };
  };

  const handleCategorySort = (field) => {
    if (categorySortField === field) {
      // Toggle direction if same field
      setCategorySortDir(categorySortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to desc for numbers, asc for name
      setCategorySortField(field);
      setCategorySortDir(field === 'name' ? 'asc' : 'desc');
    }
  };

  const getSortedCategories = () => {
    if (!stats?.categories?.all) return [];
    
    return [...stats.categories.all].sort((a, b) => {
      let comparison = 0;
      
      if (categorySortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (categorySortField === 'total') {
        comparison = a.total - b.total;
      } else if (categorySortField === 'accuracy') {
        comparison = a.accuracy - b.accuracy;
      }
      
      return categorySortDir === 'asc' ? comparison : -comparison;
    });
  };

  const SortIcon = ({ field }) => {
    if (categorySortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-500" />;
    }
    return categorySortDir === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-[hsl(var(--brand-gold-500))]" />
      : <ArrowDown className="w-3 h-3 text-[hsl(var(--brand-gold-500))]" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading statistics...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <Card className="p-8 text-center bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <p className="text-white mb-4">No statistics available yet.</p>
          <Link to="/sprint">
            <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
              Start Your First Sprint
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12 shrink-0" />
              <h1 className="text-lg sm:text-xl font-display text-white hidden sm:block">Sprint Statistics</h1>
            </div>
            <Link to="/sprint">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-sm shrink-0">
                <Zap className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Sprint</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-3xl font-bold text-white">{stats.overview.total_sessions}</p>
              <p className="text-gray-400 text-sm">Sessions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Brain className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className={`text-3xl font-bold ${getAccuracyColor(stats.overview.overall_accuracy)}`}>
                {stats.overview.overall_accuracy}%
              </p>
              <p className="text-gray-400 text-sm">Accuracy</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 mx-auto mb-2 text-orange-400" />
              <p className="text-3xl font-bold text-white">{stats.overview.current_streak}</p>
              <p className="text-gray-400 text-sm">Day Streak</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-3xl font-bold text-white">{formatPlayTime(stats.overview.total_time_played_seconds)}</p>
              <p className="text-gray-400 text-sm">Time Played</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] p-1 w-full flex">
            <TabsTrigger 
              value="overview" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <BarChart3 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger 
              value="categories" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <Target className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Categories</span>
            </TabsTrigger>
            <TabsTrigger 
              value="achievements" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <Award className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Achievements</span>
            </TabsTrigger>
            <TabsTrigger 
              value="review" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <BookOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Review</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Performance Trend */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    {getTrendIcon(stats.trends.accuracy_trend)}
                    Recent Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.trends.last_7_sessions.length > 0 ? (
                    <div className="space-y-3">
                      {stats.trends.last_7_sessions.map((session, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-16 text-sm text-gray-300">
                            {session.date ? new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                          </div>
                          <div className="flex-1 h-2 rounded-full overflow-hidden bg-[hsl(var(--accent))]">
                            <div 
                              className="h-full rounded-full transition-all" 
                              style={{ 
                                width: `${session.accuracy}%`,
                                backgroundColor: session.accuracy >= 60 ? '#34d399' : session.accuracy >= 40 ? '#fbbf24' : '#fb7185'
                              }} 
                            />
                          </div>
                          <div className={`w-12 text-right font-mono ${getAccuracyColor(session.accuracy)}`}>
                            {session.accuracy}%
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-300">No recent sessions</p>
                  )}
                </CardContent>
              </Card>

              {/* Speed Stats */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    Speed Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Average Response</span>
                    <span className="text-white font-mono">{formatTime(stats.speed_stats.average_time_ms)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Fastest Correct</span>
                    <span className="text-emerald-400 font-mono">{formatTime(stats.speed_stats.fastest_correct_ms)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Quick Draws (&lt;3s)</span>
                    <span className="text-[hsl(var(--brand-gold-400))] font-mono">{stats.speed_stats.quick_draw_count}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Difficulty Breakdown */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    Difficulty Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(stats.difficulty_breakdown).map(([diff, data]) => (
                    <div key={diff} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-200 capitalize">{diff}</span>
                        <span className={getAccuracyColor(data.accuracy)}>{data.accuracy}%</span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden bg-[hsl(var(--accent))]">
                        <div 
                          className="h-full rounded-full" 
                          style={{ 
                            width: `${data.accuracy}%`,
                            backgroundColor: data.accuracy >= 60 ? '#34d399' : data.accuracy >= 40 ? '#fbbf24' : '#fb7185'
                          }} 
                        />
                      </div>
                      <p className="text-xs text-gray-500">{data.correct}/{data.total} correct • {data.sessions} sessions</p>
                    </div>
                  ))}
                  {Object.keys(stats.difficulty_breakdown).length === 0 && (
                    <p className="text-gray-300">No difficulty data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Streaks */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    Streaks & Records
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Current Streak</span>
                    <span className="text-orange-400 font-bold text-xl">{stats.overview.current_streak} days</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Best Streak</span>
                    <span className="text-[hsl(var(--brand-gold-400))] font-bold">{stats.overview.best_streak} days</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Best Accuracy</span>
                    <span className="text-emerald-400 font-bold">{stats.overview.best_accuracy}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Total Questions</span>
                    <span className="text-white font-mono">{stats.overview.total_questions.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Top Categories */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                    Top 5 Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.categories.top_5 && stats.categories.top_5.length > 0 ? (
                    <div className="space-y-3">
                      {stats.categories.top_5.map((cat, idx) => {
                        const mastery = getMasteryLevel(cat.accuracy, cat.total);
                        return (
                          <div key={cat.name} className="p-3 rounded-lg bg-[hsl(var(--accent))]">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-white font-medium truncate max-w-[150px]">{cat.name}</span>
                              <Badge className={`${mastery.color} text-white text-xs`}>{mastery.level}</Badge>
                            </div>
                            <div className="h-2 rounded-full overflow-hidden mb-1 bg-[hsl(var(--accent))]">
                              <div 
                                className="h-full rounded-full" 
                                style={{ 
                                  width: `${cat.accuracy}%`,
                                  backgroundColor: cat.accuracy >= 60 ? '#34d399' : cat.accuracy >= 40 ? '#fbbf24' : '#fb7185'
                                }} 
                              />
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-300">{cat.correct}/{cat.total}</span>
                              <span className={getAccuracyColor(cat.accuracy)}>{cat.accuracy}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-300">Need 5+ questions per category</p>
                  )}
                </CardContent>
              </Card>

              {/* Needs Improvement */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-rose-400" />
                    Bottom 5 Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.categories.bottom_5 && stats.categories.bottom_5.length > 0 ? (
                    <div className="space-y-3">
                      {stats.categories.bottom_5.map((cat, idx) => {
                        const mastery = getMasteryLevel(cat.accuracy, cat.total);
                        const eligibleForStudyPath = cat.total >= 15 && cat.accuracy < 60;
                        return (
                          <div key={cat.name} className="p-3 rounded-lg bg-[hsl(var(--accent))]">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-white font-medium truncate max-w-[150px]">{cat.name}</span>
                              <Badge className={`${mastery.color} text-white text-xs`}>{mastery.level}</Badge>
                            </div>
                            <div className="h-2 rounded-full overflow-hidden mb-1 bg-[hsl(var(--accent))]">
                              <div 
                                className="h-full rounded-full" 
                                style={{ 
                                  width: `${cat.accuracy}%`,
                                  backgroundColor: cat.accuracy >= 60 ? '#34d399' : cat.accuracy >= 40 ? '#fbbf24' : '#fb7185'
                                }} 
                              />
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-300">{cat.correct}/{cat.total}</span>
                              <span className={getAccuracyColor(cat.accuracy)}>{cat.accuracy}%</span>
                            </div>
                            {eligibleForStudyPath && (
                              <Link to={`/study-path?category=${encodeURIComponent(cat.name)}`}>
                                <Button
                                  size="sm"
                                  className="w-full mt-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                                  data-testid={`study-path-${cat.name}`}
                                >
                                  <GraduationCap className="w-3 h-3 mr-1" />
                                  Start Study Path
                                </Button>
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-300">Need 5+ questions per category</p>
                  )}
                </CardContent>
              </Card>

              {/* All Categories */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    All Categories ({stats.categories.total_unique})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Sort Headers */}
                  <div className="flex justify-between items-center py-2 mb-2 border-b border-[hsl(var(--border))]">
                    <button 
                      onClick={() => handleCategorySort('name')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-white transition-colors"
                    >
                      Category <SortIcon field="name" />
                    </button>
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => handleCategorySort('total')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-white transition-colors"
                      >
                        # <SortIcon field="total" />
                      </button>
                      <button 
                        onClick={() => handleCategorySort('accuracy')}
                        className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-white transition-colors w-14 justify-end"
                      >
                        % <SortIcon field="accuracy" />
                      </button>
                    </div>
                  </div>
                  <ScrollArea className="h-[380px] pr-4">
                    <div className="space-y-2">
                      {getSortedCategories().map((cat, idx) => (
                        <div key={cat.name} className="flex justify-between items-center py-2 border-b border-[hsl(var(--border))]">
                          <span className="truncate max-w-[120px] text-sm text-gray-300">{cat.name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-500 w-6 text-right">{cat.total}</span>
                            <span className={`font-mono text-sm w-14 text-right ${getAccuracyColor(cat.accuracy)}`}>{cat.accuracy}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-4">
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  Achievements ({stats.achievements.total_earned}/{stats.achievements.total_available})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stats.achievements.earned.map((achievement, idx) => (
                    <div 
                      key={achievement.name} 
                      className="p-4 rounded-lg"
                      style={{ 
                        background: 'linear-gradient(135deg, rgba(250, 204, 21, 0.15) 0%, rgba(250, 204, 21, 0.05) 100%)',
                        border: '1px solid rgba(250, 204, 21, 0.3)'
                      }}
                    >
                      <div className="text-3xl mb-2">{achievement.icon}</div>
                      <h3 className="text-white font-bold">{achievement.name}</h3>
                      <p className="text-gray-300 text-sm">{achievement.description}</p>
                    </div>
                  ))}
                  {stats.achievements.earned.length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <Award className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                      <p className="text-gray-300">No achievements earned yet. Keep playing!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Review Tab - Missed Questions */}
          <TabsContent value="review" className="space-y-4">
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  Review Missed Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.missed_questions.length > 0 ? (
                  <div className="space-y-4">
                    {stats.missed_questions.map((q, idx) => (
                      <div 
                        key={q.question} 
                        className="p-4 rounded-lg bg-[hsl(var(--accent))] border-l-4 border-rose-400"
                      >
                        <Badge className="mb-2 bg-[hsl(var(--brand-blue-800))] text-[hsl(var(--brand-gold-500))]">{q.category}</Badge>
                        <p className="text-white mb-3">{q.question}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-300">Your answer: </span>
                            <span className="text-rose-400">{q.player_answer || '(no answer)'}</span>
                          </div>
                          <div>
                            <span className="text-gray-300">Correct: </span>
                            <span className="text-emerald-400">{q.correct_answer}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
                    <p className="text-gray-300">No missed questions to review. Great job!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
